/**
 * The one control on this product that ships customer data outward.
 *
 * It is always a user action — nothing in Lookout may press it on a user's behalf (the brief's rule:
 * "the AI never presses the button that ships data"). The four visual states are driven entirely by
 * `useRunSync`'s machine, which advances on server-confirmed polls rather than on the click, so the
 * button never claims a transition the platform has not acknowledged.
 *
 * Ported from rudder-webapp's `syncDetailsPage/components/runSyncButton.tsx`, including the stop
 * dialog's verbatim wording. The full-sync confirmation's wording is the webapp's too, from
 * `dataGraphAudiences/syncs/components/syncContextMenu.tsx`.
 */
import { Ban, ChevronDown, Play } from 'lucide-react';
import { useState } from 'react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { ConfirmDialog } from '@/_shared/components/ConfirmDialog';
import { Button, ButtonGroup, ButtonGroupSeparator } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';

import type { RunButtonState } from '../hooks/useRunSync';
import { fullSyncConfirmationFor } from '../lib/full-sync-copy';

type SyncType = 'full' | 'incremental';

interface RunSyncButtonProps {
  state: RunButtonState;
  /** `sync.enabled && setup is ready`. */
  canStart: boolean;
  /** Why it cannot start, when it cannot. Empty while `canStart`. */
  denialReason: string;
  /** The activation's destination definition; picks the full-sync confirmation copy. */
  destinationDefinitionName?: string | null;
  onStart: (syncType: SyncType) => void;
  onStop: () => void;
}

/**
 * The Stop control and its confirmation, as their own component so the "confirm is open" flag lives
 * and dies with the RUNNING state.
 *
 * Held in the parent it would outlive the run: a run that finishes on its own while the dialog is
 * open unmounts the dialog with the flag still set, and the next run — a cron fire nobody pressed a
 * button for — would reopen a modal asking the user to stop it.
 */
function StopRunControl({ onStop }: { onStop: () => void }): React.ReactElement {
  const [confirming, setConfirming] = useState(false);

  return (
    <>
      <Button
        variant="secondary-destructive"
        Icon={<Ban />}
        label="Stop run"
        onClick={() => {
          setConfirming(true);
        }}
      />
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Hold up!"
        description="Are you sure you want to stop the sync?"
        confirmLabel="Stop run"
        onConfirm={() => {
          setConfirming(false);
          onStop();
        }}
      />
    </>
  );
}

function StartRunControl({
  canStart,
  denialReason,
  destinationDefinitionName,
  onStart,
}: Pick<
  RunSyncButtonProps,
  'canStart' | 'denialReason' | 'onStart' | 'destinationDefinitionName'
>): React.ReactElement {
  const [confirmingFullSync, setConfirmingFullSync] = useState(false);

  return (
    <>
      <ActionTooltip show={!canStart} message={denialReason}>
        <ButtonGroup>
          <Button
            variant="primary"
            Icon={<Play />}
            label="Run sync"
            disabled={!canStart}
            onClick={() => onStart('incremental')}
          />
          <ButtonGroupSeparator />
          {canStart ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="primary" iconOnly aria-label="Run sync options" Icon={<ChevronDown />} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setConfirmingFullSync(true)}>Run full sync</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="primary" iconOnly aria-label="Run sync options" Icon={<ChevronDown />} disabled />
          )}
        </ButtonGroup>
      </ActionTooltip>
      <ConfirmDialog
        open={confirmingFullSync}
        onOpenChange={setConfirmingFullSync}
        title="Run full sync?"
        description={fullSyncConfirmationFor(destinationDefinitionName)}
        confirmLabel="Run full sync"
        destructive={false}
        onConfirm={() => {
          setConfirmingFullSync(false);
          onStart('full');
        }}
      />
    </>
  );
}

export function RunSyncButton({
  state,
  canStart,
  denialReason,
  destinationDefinitionName,
  onStart,
  onStop,
}: RunSyncButtonProps): React.ReactElement {
  if (state === 'starting' || state === 'stopping') {
    return <Button variant="secondary" loading label={state === 'starting' ? 'Starting...' : 'Stopping...'} />;
  }

  if (state === 'running') {
    return <StopRunControl onStop={onStop} />;
  }

  // Idle. Disabled-with-a-reason rather than hidden — a missing button is a question the user
  // cannot answer. Keyed on `canStart` so losing the right to start remounts the control, and the
  // remount is what closes an open full-sync confirmation: a blocked control renders no menu, so no
  // confirm can fire from it.
  return (
    <StartRunControl
      key={canStart ? 'startable' : 'blocked'}
      canStart={canStart}
      denialReason={denialReason}
      destinationDefinitionName={destinationDefinitionName}
      onStart={onStart}
    />
  );
}
